import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PeoplyLogo } from '@/components/PeoplyLogo';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Card } from '@/components/ui/card';
import { AUTH_SHELL_VARIANTS } from '@/constants/login';
import type { AuthShellVariant } from '@/types/authShell';
import { AuthAccountTypeLegend, AuthShowcasePanel, AuthSignupSteps } from './AuthShowcasePanel';

/**
 * The frame of the sign-in / sign-up pages shared by candidates and companies (Giriş / Kayıt
 * tasarımları): brand header, the dark brand panel for the variant on the left, the form on the
 * right and an optional note under it.
 */
export const AuthShell = ({
  variant,
  children,
  footer,
}: {
  variant: AuthShellVariant;
  children: ReactNode;
  footer?: ReactNode;
}) => {
  const { t } = useTranslation();
  const base = `auth.showcase.${variant}`;
  const list = (key: string) => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? (value as string[]) : [];
  };

  return (
    <div className='min-h-screen bg-public-warm font-[Geist,ui-sans-serif,system-ui,sans-serif]'>
      <header className='mx-auto flex max-w-[1280px] items-center justify-between px-5 pt-[22px] lg:px-10'>
        <Link to='/' className='flex items-center gap-[9px]'>
          <PeoplyLogo className='h-[29px] w-8' aria-hidden='true' />
          <span className='text-[23px] font-semibold tracking-[-.025em] text-public-ink'>Peoply</span>
        </Link>
        <LanguageSwitcher variant='auth' />
      </header>

      <main className='mx-auto max-w-[1280px] px-4 pb-5 pt-[22px] lg:px-5'>
        <Card className='grid overflow-hidden rounded-[26px] border-[var(--public-line-faint)] bg-public-paper shadow-[0_40px_80px_-50px_rgba(14,20,36,.45)] lg:min-h-[865px] lg:grid-cols-[560px_1fr]'>
          <AuthShowcasePanel
            title={t(`${base}.title`)}
            highlight={t(`${base}.highlight`)}
            description={t(`${base}.text`)}
            features={list(`${base}.points`)}
            note={variant === AUTH_SHELL_VARIANTS.login ? t('auth.showcase.login.legendNote') : undefined}
          >
            {variant === AUTH_SHELL_VARIANTS.login ? (
              <AuthAccountTypeLegend
                rows={[
                  { label: t('auth.login.tabCandidate'), description: t('auth.showcase.login.legendCandidate') },
                  { label: t('auth.login.tabEmployer'), description: t('auth.showcase.login.legendEmployer') },
                ]}
              />
            ) : (
              <AuthSignupSteps
                title={t('auth.showcase.stepsTitle')}
                here={t('auth.showcase.stepsHere')}
                steps={list(`${base}.steps`)}
              />
            )}
          </AuthShowcasePanel>

          <section className='px-6 py-10 sm:px-12 lg:px-20 lg:pb-10 lg:pt-[60px]'>
            <div className='mx-auto max-w-[520px] lg:mx-0'>
              {children}
              {footer && <p className='mt-[26px] text-center text-xs leading-relaxed text-public-dim'>{footer}</p>}
            </div>
          </section>
        </Card>
      </main>
    </div>
  );
};
